import Vue from 'vue';
import {Constants} from '@/const';
import { System } from '@/models/dtos';
import { StoreObjectStatus, StoreObject, StoreObjectList } from '@/store/storeObject';
import { StateObject } from '@/store/stateObject';


/**
 * The input, either hash calculated from parameters or id.
 */
export class StateInput {
	public paramsHash: string | null = null;
	public id: string | number | null = null;
	constructor(paramsHash: string | null = null, id: string | number | null = null) {
		this.paramsHash = paramsHash;
		this.id = id;
	}

	/**
	 * Checks if current statInput object is valid, throw exception if not.
	 */
	public checkValidity() {
		if (this.paramsHash == null && this.id == null) {
			throw new Error('Missing both params and id, please include 1 of them');
		}
	}
}

/**
 * Common store utils. Do not reuse anywhere, only for store
 */
export class StoreUtils {
	public static getStoreObjectById<T>(state: StateObject<T>, id: string | number): StoreObject<T> {
		return StoreUtils.getDataObject<T>(state, new StateInput(null, id));
	}

	/**
	 * Gets a list of items by paramsHash
	 * @param state The current state
	 * @param paramsHash The hash created by the parameters
	 */
	public static getStoreObjectListByParamHash<T>(state: StateObject<T>, paramsHash: string): StoreObjectList<T> {
		return StoreUtils.getDataObject<T>(state, new StateInput(paramsHash));
	}
	/**
	 * Sets pending
	 * @param {object} state
	 */
	public static setPending<T>(state: StateObject<T>, input: StateInput) {
		StoreUtils.setStatus<T>(state, StoreObjectStatus.Pending, input);
	}

	/**
	 * Sets success state to state object and sets data
	 * @param state The state
	 * @param data The data either list of object
	 * @param paramsHash {string} The parameter if exists.
	 * @param id {string|number} The id if exists
	 */
	public static setSuccess<T>(state: StateObject<T>, data: T | T[], input: StateInput) {
		StoreUtils.setStatus<T>(state, StoreObjectStatus.Success, input, data);
	}

	/**
	 * Sets error state to state object and sets error
	 * @param state The current state
	 * @param error
	 */
	public static setError<T>(state: any, error: any, input: StateInput) {
		StoreUtils.setStatus<T>(state, StoreObjectStatus.Error, input, null, error);
	}

	/**
	 * Adds common params to existing params
	 * @returns {object} The new params
	 */
	public static getParams(params: any, state: any) {
		params.systemId = StoreStatic.systemData.id;
		params.culture = StoreStatic.systemData.culture;
		return params;
	}

	/**
	 * Gets Id-dictionary from state, if NULL new is created,
	 * @param state The current state
	 * @returns {object} Dictionary with id as key.
	 */
	private static getDictionaryFromState<T>(state: StateObject<T>): {[key: string]: StoreObject<T>} {
		if (state.dictionary != null) {
			return state.dictionary;
		}
		const dictionary = {};
		Vue.set(state, 'dictionary', dictionary);
		return dictionary;
	}
	/**
	 * Gets params-dictionary from state, if NULL new is created,
	 * @param state The current state
	 * @returns {object} Dictionary with paramsHash as key.
	 */
	private static getParamsDictionaryFromState<T>(state: StateObject<T>): {[key: string]: StoreObjectList<T>} {
		if (state.paramsDictionary != null) {
			return state.paramsDictionary;
		}
		const paramsDictionary = {} as {[key: string]: StoreObjectList<T>};
		Vue.set(state, 'paramsDictionary', paramsDictionary);
		return paramsDictionary;
	}

	/**
	 * Sets state status and data if exists
	 * @param state The current state
	 * @param status The current status of process (success, error, pending)
	 * @param input The input object
	 * @param data The data if success
	 * @param error The error if error
	 */
	private static setStatus<T>(
		state: StateObject<T>,
		status: StoreObjectStatus, input: StateInput, data: T | T[] | null = null, error: any | null = null) {
		input.checkValidity();

		const obj: any = {
			status,
			error,
			data,
		};

		if (input.paramsHash != null) {
			Vue.set(StoreUtils.getParamsDictionaryFromState<T>(state), input.paramsHash, obj);

			// If success, we also set the idDictionary-data
			if (status === StoreObjectStatus.Success) {
				const dictionary = StoreUtils.getDictionaryFromState<T>(state);

				if (Array.isArray(obj.data)) {
					obj.data.forEach((item: any) => {
						Vue.set(dictionary, item.id, {
							status,
							error: null,
							data: item,
						});
					});
				}
			}
		} else if (input.id != null) {
			Vue.set(StoreUtils.getDictionaryFromState<T>(state), input.id, obj);
		}
	}

	/**
	 * Gets data object from state
	 * @param state The current state
	 * @param paramsHash The paramsHash if params is used
	 * @param id The id if used
	 * @returns {object | Array} If id is used, it returns object if exists, else array if exists.
	 */
	private static getDataObject<T>(state: StateObject<T>, input: StateInput): any | any[] | null {
		input.checkValidity();

		if (input.paramsHash != null) {
			return StoreUtils.getParamsDictionaryFromState(state)[input.paramsHash];
		} else if (input.id != null) {
			return StoreUtils.getDictionaryFromState<T>(state)[input.id];
		}
	}
	/**
	 * Gets data object by id if exists
	 * @param state The current state
	 * @param id The id of the item
	 */
}

/**
 * Static data for system, set on main load.
 */
export class StoreStatic {
	public static systemData: System;
}
